import React, { useState, useEffect, Fragment } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Tooltip,
  Typography,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Message as MessageIcon,
  Search as SearchIcon,
  Logout as LogoutIcon,
  ChevronLeft as ChevronLeftIcon,
  LinkedIn as LinkedInIcon,
  Google as GoogleIcon,
  Key as KeyIcon,
  AccessTime as AccessTimeIcon,
  School as SchoolIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useConversationRefresh } from '../contexts/ConversationRefreshContext';
import InactivityWrapper from './InactivityWrapper';
import GoogleProfileImage from './GoogleProfileImage';
import useBackendHealth from '../hooks/useBackendHealth';

const COLLAPSED_WIDTH = 60;
const EXPANDED_WIDTH = 240;

const LayoutNew = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, timezone } = useAuth();
  const { refreshConversations } = useConversationRefresh();
  const { status: backendHealthStatus } = useBackendHealth(10000); // Check every 10s
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [academicMenuOpen, setAcademicMenuOpen] = useState(false);
  const [academicPopupAnchor, setAcademicPopupAnchor] = useState(null);

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', collapsed);
  }, [collapsed]);
  
  // Load the user profile if it exists
  useEffect(() => {
    const profileData = localStorage.getItem('userProfile');
    if (profileData) {
      try {
        const profile = JSON.parse(profileData);
        console.log('Loaded user profile:', profile);
        console.log('Picture URL:', profile.picture);
        setUserProfile(profile);
      } catch (e) {
        console.error('Error parsing user profile:', e);
        setUserProfile(null);
      }
    } else {
      // Si no hay perfil de Google, limpiar el estado
      setUserProfile(null);
    }
  }, [user]);

  // Auto-collapse when entering conversations
  useEffect(() => {
    if (location.pathname.startsWith('/conversations')) {
      setCollapsed(true);
    }
  }, [location.pathname]);

  const handleToggleSidebar = () => {
    setCollapsed(!collapsed);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNavigation = (path) => {
    // If we go to conversations, refresh the list
    if (path === '/conversations') {
      refreshConversations();
    }
    navigate(path);
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Conversations', icon: <MessageIcon />, path: '/conversations' },
    { text: 'Search', icon: <SearchIcon />, path: '/search' },
    {
      text: 'Academic Analytics',
      icon: <SchoolIcon />,
      isSubmenu: true,
      submenu: [
        { text: 'Live Class', path: '/academic/live' },
        { text: 'Class Analytics', path: '/academic/class-analytics' },
        { text: 'Transcript AI', path: '/academic/transcript' },
        { text: 'NPS & Experience', path: '/academic/nps' },
        { text: 'Overview', path: '/academic/overview' },
        { text: '─────────', path: null, divider: true },
        { text: 'Showcases:', path: null, header: true },
        { text: 'Funnel Analytics', path: '/funnel-showcase' },
        { text: 'Recharts Demo', path: '/charts-showcase' },
        { text: 'Visx (Airbnb)', path: '/visx-showcase' },
        { text: 'Google Charts', path: '/google-charts-showcase' },
        { text: 'ECharts', path: '/echarts-showcase' },
        { text: 'Gantt Timeline', path: '/gantt-showcase' },
      ]
    },
  ];

  const sidebarWidth = collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH;

  return (
    <InactivityWrapper>
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar Colapsable */}
      <Box
        sx={{
          width: sidebarWidth,
          height: '100vh',
          bgcolor: '#1a3a70',
          color: 'white',
          transition: 'width 0.3s ease',
          display: 'flex',
          flexDirection: 'column',
          position: 'fixed',
          left: 0,
          top: 0,
          zIndex: 1200,
          overflowY: 'auto',
        }}
      >
        {/* Header del Sidebar */}
        <Box 
          sx={{ 
            py: 1.5,
            px: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'space-between',
            height: 56,
            overflow: 'hidden',
            boxSizing: 'border-box',
          }}
        >
          {collapsed ? (
            <IconButton 
              onClick={handleToggleSidebar}
              sx={{ 
                color: 'white',
                p: 1,
              }}
            >
              <img 
                src="/assets/branding/guayerd-isologo.svg" 
                alt="Guayerd" 
                width={36} 
                height={36} 
              />
            </IconButton>
          ) : (
            <>
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  cursor: 'pointer',
                  flexGrow: 1,
                  minWidth: 0,
                  '&:hover': {
                    opacity: 0.8,
                  }
                }}
                onClick={handleToggleSidebar}
              >
                <img 
                  src="/assets/branding/guayerd-logo-white.svg" 
                  alt="Guayerd" 
                  width={36} 
                  height={36} 
                  style={{ marginRight: 12, flexShrink: 0 }} 
                />
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700, 
                    fontSize: '1.1rem',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                  }}
                >
                  GuayerdOS
                </Typography>
              </Box>
              <IconButton 
                onClick={handleToggleSidebar}
                sx={{ 
                  color: 'white',
                  p: 0.5,
                }}
                size="small"
              >
                <ChevronLeftIcon />
              </IconButton>
            </>
          )}
        </Box>

        <Divider sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />

        {/* Menu Items */}
        <List sx={{ flexGrow: 1, py: 2 }}>
          {menuItems.map((item) => {
            if (item.isSubmenu) {
              const isAnySubSelected = item.submenu.some(sub => location.pathname.startsWith(sub.path));
              
              const submenuButton = (
                <ListItemButton
                  onClick={(e) => {
                    if (collapsed) {
                      setAcademicPopupAnchor(e.currentTarget);
                    } else {
                      setAcademicMenuOpen(!academicMenuOpen);
                    }
                  }}
                  onMouseEnter={(e) => {
                    if (collapsed) {
                      setAcademicPopupAnchor(e.currentTarget);
                    }
                  }}
                  sx={{
                    minHeight: 48,
                    justifyContent: collapsed ? 'center' : 'initial',
                    px: collapsed ? 1 : 2.5,
                    mx: collapsed ? 1 : 2,
                    borderRadius: 2,
                    color: 'white',
                    bgcolor: isAnySubSelected ? 'rgba(255,255,255,0.1)' : 'transparent',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.15)',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: collapsed ? 0 : 3,
                      justifyContent: 'center',
                      color: 'white',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {!collapsed && (
                    <>
                      <Typography variant="body1" sx={{ flex: 1 }}>
                        {item.text}
                      </Typography>
                      {academicMenuOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </>
                  )}
                </ListItemButton>
              );
              
              return (
                <Fragment key={item.text}>
                  <ListItem disablePadding sx={{ display: 'block' }}>
                    {submenuButton}
                  </ListItem>
                  
                  {/* Submenu inline (when expanded) */}
                  {!collapsed && academicMenuOpen && item.submenu.map((subitem) => {
                    // Divider
                    if (subitem.divider) {
                      return (
                        <Divider key={subitem.text} sx={{ my: 1, mx: 2, bgcolor: 'rgba(255,255,255,0.1)' }} />
                      );
                    }
                    // Header
                    if (subitem.header) {
                      return (
                        <ListItem key={subitem.text} sx={{ py: 1, px: 2 }}>
                          <Typography variant="caption" sx={{ 
                            color: 'rgba(255,255,255,0.5)', 
                            fontSize: '0.7rem', 
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            pl: 3,
                          }}>
                            {subitem.text}
                          </Typography>
                        </ListItem>
                      );
                    }
                    // Normal menu item
                    return (
                      <ListItem key={subitem.text} disablePadding>
                        <ListItemButton
                          selected={location.pathname === subitem.path}
                          onClick={() => handleNavigation(subitem.path)}
                          sx={{
                            minHeight: 40,
                            pl: 5,
                            mx: 2,
                            borderRadius: 2,
                            color: 'white',
                            '&.Mui-selected': {
                              bgcolor: 'rgba(255,255,255,0.15)',
                              '&:hover': {
                                bgcolor: 'rgba(255,255,255,0.2)',
                              },
                            },
                            '&:hover': {
                              bgcolor: 'rgba(255,255,255,0.1)',
                            },
                          }}
                        >
                          <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                            {subitem.text}
                          </Typography>
                        </ListItemButton>
                      </ListItem>
                    );
                  })}

                  {/* Popup menu (when collapsed) */}
                  {collapsed && (
                    <Menu
                      anchorEl={academicPopupAnchor}
                      open={Boolean(academicPopupAnchor)}
                      onClose={() => setAcademicPopupAnchor(null)}
                      anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                      }}
                      PaperProps={{
                        sx: {
                          bgcolor: '#1a3a70',
                          color: 'white',
                          ml: 0.5,
                          minWidth: 200,
                          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                        }
                      }}
                      MenuListProps={{
                        onMouseLeave: () => setAcademicPopupAnchor(null),
                      }}
                    >
                      <Box sx={{ px: 2, py: 1, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', fontSize: '0.7rem', fontWeight: 600 }}>
                          {item.text}
                        </Typography>
                      </Box>
                      {item.submenu.map((subitem) => {
                        // Divider
                        if (subitem.divider) {
                          return <Divider key={subitem.text} sx={{ my: 0.5, bgcolor: 'rgba(255,255,255,0.1)' }} />;
                        }
                        // Header
                        if (subitem.header) {
                          return (
                            <Box key={subitem.text} sx={{ px: 2, py: 1 }}>
                              <Typography variant="caption" sx={{ 
                                color: 'rgba(255,255,255,0.5)', 
                                fontSize: '0.65rem', 
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                              }}>
                                {subitem.text}
                              </Typography>
                            </Box>
                          );
                        }
                        // Normal menu item
                        return (
                          <MenuItem
                            key={subitem.text}
                            onClick={() => {
                              handleNavigation(subitem.path);
                              setAcademicPopupAnchor(null);
                            }}
                            selected={location.pathname === subitem.path}
                            sx={{
                              color: 'white',
                              '&.Mui-selected': {
                                bgcolor: 'rgba(255,255,255,0.15)',
                              },
                              '&:hover': {
                                bgcolor: 'rgba(255,255,255,0.1)',
                              },
                            }}
                          >
                            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                              {subitem.text}
                            </Typography>
                          </MenuItem>
                        );
                      })}
                    </Menu>
                  )}
                </Fragment>
              );
            }

            const isSelected = location.pathname.startsWith(item.path);
            const button = (
              <ListItemButton
                selected={isSelected}
                onClick={() => handleNavigation(item.path)}
                sx={{
                  minHeight: 48,
                  justifyContent: collapsed ? 'center' : 'initial',
                  px: collapsed ? 1 : 2.5,
                  mx: collapsed ? 1 : 2,
                  borderRadius: 2,
                  color: 'white',
                  '&.Mui-selected': {
                    bgcolor: 'rgba(255,255,255,0.15)',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.2)',
                    },
                  },
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: collapsed ? 0 : 3,
                    justifyContent: 'center',
                    color: 'white',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {!collapsed && (
                  <Typography variant="body1">
                    {item.text}
                  </Typography>
                )}
              </ListItemButton>
            );

            return (
              <ListItem key={item.text} disablePadding sx={{ display: 'block' }}>
                {collapsed ? (
                  <Tooltip title={item.text} placement="right">
                    {button}
                  </Tooltip>
                ) : (
                  button
                )}
              </ListItem>
            );
          })}
        </List>

        {/* User Profile */}
        <Box sx={{ p: collapsed ? 1 : 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: collapsed ? 'center' : 'space-between',
              p: 1,
              borderRadius: 2,
              bgcolor: 'rgba(255,255,255,0.1)',
              cursor: 'pointer',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.15)',
              },
            }}
            onClick={handleProfileMenuOpen}
          >
            <GoogleProfileImage 
              src={userProfile?.picture}
              fallbackText={(userProfile?.name || user)?.charAt(0).toUpperCase()}
              healthStatus={backendHealthStatus}
              sx={{ 
                width: 32, 
                height: 32, 
                fontSize: 14,
              }}
            />
            {!collapsed && (
              <Box sx={{ ml: 1, flexGrow: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {userProfile?.name || user}
                </Typography>
                <Typography variant="caption" sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  opacity: 0.9,
                  fontSize: '0.7rem',
                  mt: 0.25
                }}>
                  {userProfile?.provider === 'linkedin' ? (
                    <>
                      <LinkedInIcon sx={{ fontSize: 12, color: '#0077B5' }} />
                      <span style={{ color: '#0077B5' }}>LinkedIn</span>
                    </>
                  ) : userProfile?.provider === 'google' ? (
                    <>
                      <GoogleIcon sx={{ fontSize: 12, color: '#4285F4' }} />
                      <span style={{ color: '#4285F4' }}>Google</span>
                    </>
                  ) : (
                    <>
                      <KeyIcon sx={{ fontSize: 12, color: '#666' }} />
                      <span style={{ color: '#666' }}>Access Key</span>
                    </>
                  )}
                </Typography>
              </Box>
            )}
          </Box>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            PaperProps={{
              sx: {
                minWidth: 280,
                mt: 1
              }
            }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <GoogleProfileImage 
                  src={userProfile?.picture}
                  fallbackText={(userProfile?.name || user)?.charAt(0).toUpperCase()}
                  healthStatus={backendHealthStatus}
                  sx={{ 
                    width: 48, 
                    height: 48, 
                    mr: 2
                  }}
                />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    {userProfile?.name || user}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {userProfile?.email || 'Access Code User'}
                  </Typography>
                  
                  {/* Login Method Badge - Elegant Style */}
                  <Box sx={{ mt: 1 }}>
                    {userProfile?.provider === 'linkedin' ? (
                      <Typography variant="caption" sx={{ 
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        color: '#0077B5',
                        fontSize: '0.75rem',
                        fontWeight: 500
                      }}>
                        <LinkedInIcon sx={{ fontSize: 16 }} />
                        Signed in with LinkedIn
                      </Typography>
                    ) : userProfile?.provider === 'google' ? (
                      <Typography variant="caption" sx={{ 
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        color: '#4285F4',
                        fontSize: '0.75rem',
                        fontWeight: 500
                      }}>
                        <GoogleIcon sx={{ fontSize: 16 }} />
                        Signed in with Google
                      </Typography>
                    ) : (
                      <Typography variant="caption" sx={{ 
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        color: '#666',
                        fontSize: '0.75rem',
                        fontWeight: 500
                      }}>
                        <KeyIcon sx={{ fontSize: 16 }} />
                        Signed in with Access Key
                      </Typography>
                    )}
                  </Box>
                  
                  {/* Timezone Info */}
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="caption" sx={{ 
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      color: '#666',
                      fontSize: '0.75rem',
                      fontWeight: 500
                    }}>
                      <AccessTimeIcon sx={{ fontSize: 14 }} />
                      {timezone}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* Main Content Area - Fixed width container */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          position: 'relative',
          maxWidth: '100%',
          width: '100%',
          marginLeft: `${sidebarWidth}px`,
          transition: 'margin-left 0.3s ease',
        }}
      >
        <Outlet />
      </Box>
    </Box>
    </InactivityWrapper>
  );
};

export default LayoutNew;
